import React from "react";
import { buildDetailPath } from "@shared/navigation/tabs/detailRoutes";
import GenericCard from "@ui-components/Card/GenericCard";
import {
  GenericTable,
  GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { useCachedFetch } from "@hooks/useCachedFetch";
import ServiceStatusValue from "kontrolEdge/components/Shared/ServiceStatusValue";
import {
  resolveServiceStatusTag,
  serviceStatusRank,
} from "kontrolEdge/helpers/serviceStatusTag";
import { DetailLink } from "../Shared/DetailLink";
import { fetchExtenderPopConnectionsAPI } from "./requests/request";
import {
  connectionDetailPath,
  connectionTypeLabel,
  shapeConnections,
  type ExtenderPopConnection,
} from "./extenderPopConnectionsData";

interface ExtenderPopConnectionsProps {
  extenderPopId: number;
  tenantId: number;
  /** The site's CPEs, to name the one behind each connection. */
  cpes: { id: number; serviceId: string }[];
  /** Only the connections of this CPE, for its own detail; the CPE column goes. */
  cpeId?: number;
}

/**
 * The site's logical connections, one fetch shared by the site's summary and
 * each CPE's list (same cache entry).
 */
export const useExtenderPopConnections = (
  extenderPopId: number,
  tenantId: number
) => {
  const api = useAxiosAuth0();
  return useCachedFetch<ExtenderPopConnection[]>(
    `kontroledge/extender-pops/${extenderPopId}/connections?tenantId=${tenantId}`,
    async () => {
      const response = await fetchExtenderPopConnectionsAPI(
        api,
        extenderPopId,
        tenantId
      );
      if (!response.data?.success) {
        throw new Error(
          response.data?.message || "Could not load the connections"
        );
      }
      return shapeConnections(response.data.data);
    }
  );
};

/**
 * The logical connections of a CPE of an Extended PoP (CTX-797): the
 * Internet, DCI, Cloud and Firewall services it delivers, each with its
 * status and the port it rides on. The ports are the way in, not the
 * content, so they are a column here rather than the section itself.
 * Without `cpeId` it lists the whole site, with the CPE behind each one.
 */
export default function ExtenderPopConnections({
  extenderPopId,
  tenantId,
  cpes,
  cpeId,
}: ExtenderPopConnectionsProps) {
  const { data, isLoading } = useExtenderPopConnections(extenderPopId, tenantId);
  const connections = (data ?? []).filter(
    (connection) => cpeId === undefined || connection.cpeId === cpeId
  );

  const cpeServiceId = new Map(cpes.map((cpe) => [cpe.id, cpe.serviceId]));

  const columns: GenericTableColumn<ExtenderPopConnection>[] = [
    {
      header: "Service ID",
      accessor: "serviceId",
      renderCell: (row) => {
        const to = connectionDetailPath(row);
        return to ? <DetailLink to={to} label={row.serviceId} /> : <div>{row.serviceId}</div>;
      },
    },
    {
      header: "Type",
      renderCell: (row) => <div>{connectionTypeLabel(row.type)}</div>,
      sortAccessor: (row) => connectionTypeLabel(row.type),
    },
    {
      header: "Name",
      renderCell: (row) => <div>{row.name ?? "-"}</div>,
      sortAccessor: (row) => row.name ?? "",
    },
    {
      header: "Status",
      renderCell: (row) => {
        const { label, variant } = resolveServiceStatusTag(row.status);
        return (
          <ServiceStatusValue
            label={label}
            variant={variant}
            tooltip={row.statusMessage ?? undefined}
          />
        );
      },
      sortAccessor: (row) => serviceStatusRank(row.status),
    },
    {
      header: "Port",
      renderCell: (row) => (
        <DetailLink
          to={buildDetailPath("port", { portId: row.port.id })}
          label={row.port.serviceId}
        />
      ),
      sortAccessor: (row) => row.port.serviceId,
    },
    ...(cpeId === undefined
      ? [
          {
            header: "Device",
            renderCell: (row: ExtenderPopConnection) => (
              <div>{(row.cpeId && cpeServiceId.get(row.cpeId)) || "-"}</div>
            ),
          },
        ]
      : []),
  ];

  return (
    <GenericCard
      isLoading={isLoading}
      headerContent="Connections"
      isCollapsible={false}
      bodyContent={
        <GenericTable
          columns={columns}
          data={connections}
          initialRowsPerPage={5}
          rowsPerPageOptions={[5, 10, 20]}
          width="100%"
          tableClassName="bg-semantic-bg-primary"
        />
      }
    />
  );
}
